import React, { useMemo } from "react";
import { motion } from "framer-motion";
import OracleScoreRing from "@/components/animation/OracleScoreRing";
import ConfidencePulse from "@/components/animation/ConfidencePulse";
import AnimatedRiskMeter from "@/components/animation/AnimatedRiskMeter";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE, biasColor, fmtNumber } from "@/components/animation/MotionShared";

const ACTION_COLORS = {
  Consider:                 "#10b981",
  Research:                 "#22d3ee",
  Watch:                    "#fcd34d",
  Avoid:                    "#ef4444",
  "Human Review Required":  "#a78bfa",
};

/**
 * Personalized market trend card — surfaces a single ranked recommendation
 * from /api/oracle/personalization/recommendations with the full 9-part
 * personalization output format (Personal Context surfaced upstream; rest is
 * inline here). Strictly additive; works alongside AnimatedMarketCard.
 */
export default function PersonalizedMarketTrendCard({
  rec,
  testid = "personalized-market-trend-card",
  onWatchlistAdd,
}) {
  const reduce = useReducedMotionSafe();
  const action = rec?.action || "Watch";
  const actionColor = ACTION_COLORS[action] || "#94a3b8";
  const fmtRange = useMemo(() => (r) => Array.isArray(r) ? r.map((x) => fmtNumber(x, 2)).join(" – ") : fmtNumber(r, 2), []);
  if (!rec) return null;
  return (
    <motion.article
      data-testid={testid}
      style={S.shell}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: "easeOut" }}
    >
      <header style={S.head}>
        <div style={S.symRow}>
          <strong style={S.symbol}>{rec.symbol}</strong>
          <span style={S.market}>{String(rec.market || "").toUpperCase()}</span>
          {rec.region && <span style={S.region}>· {rec.region}</span>}
        </div>
        <span style={{ ...S.actionPill, color: actionColor, borderColor: actionColor, background: `${actionColor}1a` }} data-testid={`${testid}-action`}>
          {action.toUpperCase()}
        </span>
      </header>

      <p style={S.why} data-testid={`${testid}-why`}>
        <strong style={S.whyLbl}>Why this fits you:</strong> {rec.why_fits_you}
      </p>

      <div style={S.body}>
        <div style={S.left}>
          <OracleScoreRing score={rec.personalized_score} size={92} label="PERSONALIZED" compact testid={`${testid}-score`} />
          <ConfidencePulse value={rec.confidence} size={56} label="CONFIDENCE" testid={`${testid}-confidence`} />
        </div>
        <div style={S.right}>
          <dl style={S.grid}>
            {rec.entry_range && (<div style={S.gridRow}><dt style={S.gridLbl}>Entry</dt><dd style={S.gridVal}>{fmtRange(rec.entry_range)}</dd></div>)}
            {rec.stop_range  && (<div style={S.gridRow}><dt style={S.gridLbl}>Stop</dt><dd style={S.gridVal}>{fmtRange(rec.stop_range)}</dd></div>)}
            {rec.target_range&& (<div style={S.gridRow}><dt style={S.gridLbl}>Target</dt><dd style={S.gridVal}>{fmtRange(rec.target_range)}</dd></div>)}
            <div style={S.gridRow}><dt style={S.gridLbl}>R:R</dt><dd style={S.gridVal}>{rec.risk_reward}</dd></div>
            <div style={S.gridRow}><dt style={S.gridLbl}>Prob band</dt><dd style={S.gridVal}>{(rec.probability_band || []).join("–")}%</dd></div>
            <div style={S.gridRow}><dt style={S.gridLbl}>Uncertainty</dt><dd style={S.gridVal}>{rec.uncertainty_pct}%</dd></div>
          </dl>
          <AnimatedRiskMeter
            level={(rec.risk_level || "medium").toLowerCase().replace(/extreme/, "high")}
            label={`RISK · ${rec.risk_level || "Medium"}`}
            testid={`${testid}-risk`}
          />
        </div>
      </div>

      {rec.assumptions && rec.assumptions.length > 0 && (
        <ul style={S.assumptions} data-testid={`${testid}-assumptions`}>
          {rec.assumptions.map((a, i) => <li key={i} style={S.assumption}>{a}</li>)}
        </ul>
      )}

      <footer style={S.foot}>
        {onWatchlistAdd && (
          <button type="button" onClick={() => onWatchlistAdd(rec)} style={S.wlBtn} data-testid={`${testid}-watchlist-add`}>
            + Add to my watchlist
          </button>
        )}
        <span style={S.compliance}>{rec.compliance_footer || "Research only · Not financial advice · Human decision required."}</span>
      </footer>
      {rec.historical_label && (
        <span style={S.historical} data-testid={`${testid}-historical-label`}>{rec.historical_label}</span>
      )}
    </motion.article>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.95))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10, color: PALETTE.slate3, boxShadow: "0 6px 22px rgba(0,0,0,0.45)", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  symRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  symbol: { color: PALETTE.goldBright, fontSize: 17, letterSpacing: "0.04em" },
  market: { color: PALETTE.gold, border: `1px solid ${PALETTE.goldGlow}`, padding: "2px 8px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em", fontWeight: 800, background: "rgba(197,160,89,0.06)" },
  region: { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.06em" },
  actionPill: { fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em", padding: "4px 11px", border: "1px solid", borderRadius: 999 },
  why: { color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.55, margin: 0 },
  whyLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", marginRight: 6 },
  body: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 10, alignItems: "center" },
  left: { display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
  right: { display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
  grid: { margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 4 },
  gridRow: { display: "flex", justifyContent: "space-between", padding: "4px 8px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6, minWidth: 0 },
  gridLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  gridVal: { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, margin: 0 },
  assumptions: { margin: "2px 0 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 },
  assumption: { color: PALETTE.slate4, fontSize: 11.5, lineHeight: 1.4, paddingLeft: 14, position: "relative" },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  wlBtn: { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  historical: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, fontStyle: "italic" },
};
